import { npcBlockIsFolded, npcBlockOf, npcBlockPreview } from '@/lib/npc-block';
import type { NpcDraft } from '@/lib/npc-draft';
import { groupedNumber } from '@/lib/npc-labels';
import type { ReplaySettings } from '@matter_extractor/shared';
import { useState } from 'react';

/**
 * « Ce que le joueur recevra » (npc.md §3.3, H8; maquette `Fiche`, planche 1) - read only.
 *
 * What a replay serves, in the order it serves it: the common rules of Réglages > Rejeu, FOLDED
 * above (they are not this NPC's and run to three thousand characters), then the block of the NPC,
 * composed of the draft on screen by `composeNpcBlock` of `shared` - the one function that decides
 * what travels, so this panel can never promise a line the server would not send. The mode's
 * paragraph (strict or completed) is the launch's choice and joins the rules then; the panel says
 * so.
 *
 * What is NOT in the block is the reason this panel exists (§3.3): the kind, the labels, the moment
 * and the expected noyau stay with the operator, and the line under the title says it - the player
 * must not know which noyau is expected of it.
 *
 * ## Folded to a preview, unfolded to the whole
 *
 * A source of eight hundred words would make the panel the longest thing on the page. Folded, each
 * text is cut and followed by « (la suite, 872 mots) »; « Tout afficher » shows the block entire,
 * word for word what `composeNpcBlock` returns.
 */

export interface NpcBlockPanelProps {
  draft: NpcDraft;
  /**
   * The rules of Réglages > Rejeu: `undefined` while they are read, `null` when the replay is
   * closed or the read failed - the panel then says it cannot show them, and shows the block.
   */
  settings: ReplaySettings | null | undefined;
}

export function NpcBlockPanel({ draft, settings }: NpcBlockPanelProps) {
  const [whole, setWhole] = useState(false);
  const folded = npcBlockIsFolded(draft);
  const block = whole || !folded ? npcBlockOf(draft) : npcBlockPreview(draft);

  return (
    <section
      data-testid="npc-bloc-joueur"
      aria-labelledby="npc-bloc-joueur-titre"
      className="space-y-3 rounded-md border-2 border-gray-200 bg-gray-50 p-5"
    >
      <div className="space-y-1">
        <h3
          id="npc-bloc-joueur-titre"
          className="font-semibold text-bleu-nuit text-body-lg leading-7"
        >
          Ce que le joueur recevra
        </h3>
        <p className="text-caption text-gray-600 leading-4">
          En lecture seule, depuis le brouillon : la consigne servie d’un rejeu de ce NPC, figée au
          démarrage avec la version. La sorte, les étiquettes, le moment et le noyau attendu n’en
          font pas partie.
        </p>
      </div>

      <details
        data-testid="npc-regles"
        className="rounded-md border border-gray-200 bg-white px-3 py-2 text-small"
      >
        <summary className="cursor-pointer font-semibold text-gray-800">
          Règles communes de Réglages &gt; Rejeu
          {settings === undefined || settings === null
            ? null
            : ` · ${groupedNumber(settings.rules.length)} caractères, repliées`}
        </summary>
        {settings === undefined ? (
          <p className="pt-2 text-gray-500 italic">Lecture des règles…</p>
        ) : settings === null ? (
          <p className="pt-2 text-gray-600">
            Les règles de Réglages &gt; Rejeu n’ont pas pu être lues.
          </p>
        ) : (
          <>
            <pre
              data-testid="npc-regles-texte"
              className="whitespace-pre-wrap pt-2 font-mono text-caption text-gray-800 leading-5"
            >
              {settings.rules}
            </pre>
            <p className="pt-2 text-caption text-gray-600 leading-4">
              Le paragraphe du mode, strict ou complété, s’ajoute à ces règles au lancement.
            </p>
          </>
        )}
      </details>

      <pre
        data-testid="npc-bloc-texte"
        className="whitespace-pre-wrap break-words rounded-md border border-gray-200 bg-white px-3 py-2.5 font-mono text-caption text-gray-900 leading-5"
      >
        {block}
      </pre>

      {folded ? (
        <button
          type="button"
          data-testid="npc-bloc-plier"
          aria-expanded={whole}
          onClick={() => setWhole((was) => !was)}
          className="cursor-pointer text-bleu-nuit text-caption underline underline-offset-2 hover:text-bleu-nuit-deep"
        >
          {whole ? 'Replier le bloc' : 'Tout afficher'}
        </button>
      ) : null}
    </section>
  );
}
